import React, { useState, useRef, useEffect } from "react";

export default function MusicPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [usingWebAudio, setUsingWebAudio] = useState(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainRef = useRef(null);

  useEffect(() => {
    // Initialize audio context for generating sound
    if (typeof window !== "undefined" && !audioContextRef.current) {
      try {
        audioContextRef.current = new (
          window.AudioContext || window.webkitAudioContext
        )();
      } catch (e) {
        console.log("Web Audio API not supported");
      }
    }
  }, []);

  const generateDummyAudio = () => {
    // Create a simple sine wave for testing
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;

    // Resume context if suspended (required by some browsers)
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.frequency.value = 440; // A4 note
    oscillator.type = "sine";

    gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 10);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 10);

    oscillator.onended = () => {
      oscillatorRef.current = null;
      if (isPlaying) {
        handleEnded();
      }
    };

    oscillatorRef.current = oscillator;
    gainRef.current = gainNode;

    return 10; // 10 seconds duration
  };

  const stopWebAudio = () => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch (e) {
        // Already stopped or stopping
      }
      oscillatorRef.current = null;
    }
    if (gainRef.current) {
      gainRef.current = null;
    }
  };

  const togglePlay = () => {
    if (audioRef.current && !usingWebAudio) {
      // Try to use actual audio file first
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(() => {
          // If file doesn't exist, switch to Web Audio API
          setUsingWebAudio(true);
          startWebAudioPlayback();
        });
        setIsPlaying(true);
      }
    } else {
      // Use Web Audio API
      if (isPlaying) {
        // Pause
        setIsPlaying(false);
        stopWebAudio();
      } else {
        // Play
        startWebAudioPlayback();
        setIsPlaying(true);
      }
    }
  };

  const startWebAudioPlayback = () => {
    setUsingWebAudio(true);
    generateDummyAudio();
  };

  const handleEnded = () => {
    setIsPlaying(false);
    stopWebAudio();
  };

  const handleError = () => {
    // File doesn't exist, switch to Web Audio API
    console.log("Audio file not found, using Web Audio API for playback");
    setUsingWebAudio(true);
  };

  return (
    <>
      <audio
        ref={audioRef}
        src="/audio/song.mp3"
        onEnded={handleEnded}
        onError={handleError}
      />

      <div className="fixed top-8 left-8 z-50">
        <div className="flex items-center gap-4 glass-widget px-5 py-3 rounded-full">
          <div
            className="w-8 h-8 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform bg-white/10 rounded-full"
            onClick={togglePlay}
          >
            <span className="material-symbols-outlined text-white text-xl">
              {isPlaying ? "pause" : "play_arrow"}
            </span>
          </div>
          <div className="flex flex-col pr-4 border-r border-white/10">
            <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">
              Now Playing
            </span>
            <span className="text-[11px] font-bold text-white tracking-widest uppercase">
              Gangster Loving
            </span>
          </div>
          <div className="flex items-end gap-[3px] h-3">
            <div
              className={`w-[2px] h-3 ${isPlaying ? "bg-white animate-pulse" : "bg-white/40"} transition-all`}
            ></div>
            <div
              className={`w-[2px] h-5 ${isPlaying ? "bg-white animate-pulse" : "bg-white"} transition-all`}
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className={`w-[2px] h-2 ${isPlaying ? "bg-white/60 animate-pulse" : "bg-white/40"} transition-all`}
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className={`w-[2px] h-4 ${isPlaying ? "bg-white animate-pulse" : "bg-white/60"} transition-all`}
              style={{ animationDelay: "0.3s" }}
            ></div>
            <div
              className={`w-[2px] h-2 ${isPlaying ? "bg-white/40 animate-pulse" : "bg-white/40"} transition-all`}
              style={{ animationDelay: "0.4s" }}
            ></div>
          </div>
        </div>
      </div>

      <div className="fixed top-8 right-8 z-50">
        <button
          className="glass-widget w-12 h-12 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors group"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <div className="flex flex-col gap-1.5">
            <span
              className={`w-5 h-[1px] bg-white transition-transform duration-300 ${isMenuOpen ? "rotate-45 translate-y-2.5" : ""}`}
            ></span>
            <span
              className={`w-5 h-[1px] bg-white transition-all duration-300 ${isMenuOpen ? "opacity-0" : "group-hover:translate-x-1"}`}
            ></span>
            <span
              className={`w-5 h-[1px] bg-white transition-transform duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-2.5" : ""}`}
            ></span>
          </div>
        </button>

        {/* Menu Overlay */}
        <div
          className={`fixed inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-500 ${isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        >
          {/* Close Button */}
          <button
            className="absolute top-8 right-8 glass-widget w-12 h-12 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors group z-10"
            onClick={() => setIsMenuOpen(false)}
          >
            <div className="flex flex-col gap-1.5">
              <span className="w-5 h-[1px] bg-white rotate-45 translate-y-2.5"></span>
              <span className="w-5 h-[1px] bg-white opacity-0"></span>
              <span className="w-5 h-[1px] bg-white -rotate-45 -translate-y-2.5"></span>
            </div>
          </button>

          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              {/* Navigation Links */}
              <nav className="mb-16">
                <ul className="space-y-8">
                  <li>
                    <a
                      href="/"
                      className="text-6xl md:text-8xl font-serif text-white hover:text-primary transition-colors duration-300 block"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Home
                    </a>
                  </li>
                  <li>
                    <a
                      href="/about"
                      className="text-6xl md:text-8xl font-serif text-white hover:text-primary transition-colors duration-300 block"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      About
                    </a>
                  </li>
                  <li>
                    <a
                      href="/discography"
                      className="text-6xl md:text-8xl font-serif text-white hover:text-primary transition-colors duration-300 block"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Discography
                    </a>
                  </li>
                </ul>
              </nav>

              {/* Social Links */}
              <div className="flex justify-center flex-wrap gap-6 md:gap-8">
                <a
                  target="_blank"
                  href="https://open.spotify.com/artist/6PKQKvMTizP7sUdDe9SfDI?si=UVhEUxZwR720GAK3SlL-ow"
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/5 hover:bg-[#1DB954]/20 border border-white/20 hover:border-[#1DB954]/40 rounded-full transition-all duration-300 group"
                  title="Spotify"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:text-[#1DB954] transition-colors"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z" />
                  </svg>
                </a>

                <a
                  target="_blank"
                  href="https://www.instagram.com/therealestt_77?igsh=MWRrOTdiMWpvY20zMg%3D%3D&utm_source=qr"
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 rounded-full transition-all duration-300 group"
                  title="Instagram"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:text-primary transition-colors"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>

                <a
                  target="_blank"
                  href="https://www.facebook.com/share/1DYTtHrMgc/?mibextid=wwXIfr"
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/5 hover:bg-[#1877F2]/20 border border-white/20 hover:border-[#1877F2]/40 rounded-full transition-all duration-300 group"
                  title="Facebook"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:text-[#1877F2] transition-colors"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                <a
                  target="_blank"
                  href="https://www.tiktok.com/@therealest_junny?_r=1&_t=ZS-94PVHrXkVK1"
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 rounded-full transition-all duration-300 group"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-white transition-colors"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
